import { ThemeOptions } from '@mui/material';
import { FormControlLabelProps as MuiFormControlLabelProps } from '@mui/material/FormControlLabel';
import MuiFormGroup from '@mui/material/FormGroup';
import MuiFormHelperText from '@mui/material/FormHelperText';
import MuiRadio, { RadioProps as MuiRadioProps } from '@mui/material/Radio';
import * as React from 'react';

import {
    FormControlLabel,
    FormControlLabelWithDescription
} from '../form-control';
import { sentence, Typography } from '../typography';
import { BaseProps, enumType, makeEnum, withVersion } from '../utils';
import { RadioGroupContext } from './context';

const sizes = ['small', 'medium', 'large'] as const;
export const RadioSize = makeEnum(...sizes);
export type RadioSize = enumType<typeof RadioSize>;

export type RadioProps = TypedOmit<MuiRadioProps, 'onChange' | 'size'> &
    BaseProps & {
        label?: React.ReactNode;
        description?: string;
        error?: React.ReactNode;
        labelPlacement?: MuiFormControlLabelProps['labelPlacement'];
        size?: RadioSize;
        onChange?(
            checked: boolean,
            event: React.ChangeEvent<HTMLInputElement>
        ): void;
    };

const RadioImpl = (props: RadioProps) => {
    const {
        label,
        description,
        error,
        labelPlacement,
        onChange,
        size,
        className,
        ...muiProps
    } = props;
    const inGroup = React.useContext(RadioGroupContext);
    const control = (
        <MuiRadio
            onChange={(e) => onChange?.(e.target.checked, e)}
            size={size === 'large' ? 'medium' : size}
            className={
                size === 'large'
                    ? `MuiRadio-sizeLarge ${className ?? ''}`
                    : className
            }
            {...muiProps}
        />
    );

    if (!label) {
        return control;
    }

    const content = (
        <>
            {description ? (
                <FormControlLabelWithDescription
                    control={control}
                    label={
                        <Typography
                            textTransform="sentence"
                            color={
                                muiProps.disabled ? 'text.disabled' : undefined
                            }
                        >
                            {label}
                        </Typography>
                    }
                    description={description}
                    labelPlacement={labelPlacement}
                />
            ) : (
                <FormControlLabel
                    control={control}
                    label={
                        <Typography
                            textTransform="sentence"
                            color={
                                muiProps.disabled ? 'text.disabled' : undefined
                            }
                        >
                            {label}
                        </Typography>
                    }
                    disableTypography
                    labelPlacement={labelPlacement}
                />
            )}
            {error && (
                <MuiFormHelperText error>{sentence(error)}</MuiFormHelperText>
            )}
        </>
    );

    return inGroup ? content : <MuiFormGroup>{content}</MuiFormGroup>;
};

export const Radio = withVersion(RadioImpl, '1.0.0');

export const radioStyle = {
    MuiRadio: {
        styleOverrides: {
            root: {
                '& input[type=radio]': {
                    marginTop: 0
                },
                '&.MuiRadio-sizeLarge .MuiSvgIcon-root': {
                    fontSize: 28
                }
            }
        }
    }
} satisfies ThemeOptions['components'];

export { RadioGroup } from './RadioGroup';
export type { RadioGroupProps } from './RadioGroup';
